// Cours premium n°2 — "Observer • Anticiper • Adapter"
// Circulation A2 — 14 chapitres pédagogiques illustrés

export const cours = {
  slug: 'deuxieme-circulation-a2',
  title: 'Ma 2ᵉ circulation A2',
  subtitle: 'Observer • Anticiper • Adapter',
  duree: '2 heures de conduite',
  mantra: 'JE CHERCHE → JE DÉTECTE → J\'ANALYSE → J\'ANTICIPE → J\'ADAPTE',
  intro: "Lors de ta première circulation, tu découvres notre méthode de travail et nous identifions tes premiers besoins. Aujourd'hui, nous allons plus loin. Tu apprends à détecter une situation suffisamment tôt pour avoir le temps de choisir ta réponse. L'enseignant reste derrière en voiture et communique par radio, mais son guidage commence progressivement à diminuer.",
  sections: [
    {
      num: '01',
      kicker: 'On repart de ton dernier bilan',
      title: 'On repart de ton dernier bilan',
      image: '/images/articles/cours2-c1.png',
      imageAlt: 'On repart de ton dernier bilan — Code 2 Roues',
      lead: 'Avant de partir, nous reprenons rapidement ta première séance :',
      type: 'timeline',
      steps: [
        { time: '5 min', label: 'Ce que tu as retenu', desc: 'Qu\'est-ce que tu as bien fait lors de la première séance ? Quelles sont tes réussites ?' },
        { time: '5 min', label: 'Ce qui reste à travailler', desc: 'Qu\'est-ce qui t\'a posé problème ? Quelles situations devons-nous encore améliorer ?' },
        { time: '5 min', label: 'Ton objectif du jour', desc: 'Aujourd\'hui, nous allons aller plus loin : observer, anticiper, analyser et adapter ton allure.' },
      ],
      retenir: 'ON NE REPART PAS DE ZÉRO. ON CONTINUE TON APPRENTISSAGE. TON DEUXIÈME COURS COMMENCE LÀ OÙ LE PREMIER S\'EST ARRÊTÉ.',
    },
    {
      num: '02',
      kicker: 'Autonomie',
      title: 'Tu reprends ton autonomie',
      image: '/images/articles/cours2-c2.png',
      imageAlt: 'Tu reprends ton autonomie — Code 2 Roues',
      lead: 'Pendant les premiers kilomètres, tu retrouves tes repères. Ton enseignant observe si les acquis de la première séance sont toujours présents.',
      type: 'list',
      list: [
        { label: 'INSTALLATION', desc: 'Confort, commandes, rétros, position.' },
        { label: 'DÉMARRAGE', desc: 'Maîtrisé, en sécurité, sans précipitation.' },
        { label: 'ALLURE', desc: 'Adaptée, régulière, cohérente.' },
        { label: 'POSITIONNEMENT', desc: 'Sur la chaussée, dans la voie.' },
        { label: 'CONTRÔLES', desc: 'Visuels, angles morts, environnement.' },
        { label: 'COMMUNICATION', desc: 'Clignotants, regards, signaux.' },
        { label: 'GROUPE', desc: 'Cohésion, distances, leader.' },
      ],
      retenir: 'LES ACQUIS D\'HIER, TA RÉUSSITE D\'AUJOURD\'HUI. À TOI DE JOUER.',
    },
    {
      num: '03',
      kicker: 'Regard',
      title: 'Ton regard prend de l\'avance',
      image: '/images/articles/cours2-c3.png',
      imageAlt: 'Ton regard prend de l\'avance — Code 2 Roues',
      lead: 'Tu ne regardes plus seulement où tu vas. Tu recherches activement les informations qui peuvent influencer ta conduite.',
      type: 'cards3',
      cards: [
        { title: 'VOIR PLUS TÔT', desc: 'J\'ouvre mon regard et je cherche l\'information.' },
        { title: 'COMPRENDRE PLUS VITE', desc: 'J\'interprète les indices et je prévois.' },
        { title: 'GARDER DES OPTIONS', desc: 'Je me donne du temps pour choisir ma réponse.' },
      ],
      retenir: 'TU NE SUBIS PLUS LA SITUATION. TU CHERCHES À LA COMPRENDRE AVANT D\'Y ARRIVER.',
    },
    {
      num: '04',
      kicker: 'Indices',
      title: 'Tu apprends à rechercher les indices',
      image: '/images/articles/cours2-c4.png',
      imageAlt: 'Tu apprends à rechercher les indices — Code 2 Roues',
      lead: 'La route te donne constamment des informations. Tu apprends à les repérer :',
      type: 'list',
      list: [
        { label: 'VÉHICULES', desc: 'Allure, position, trajectoire, feux.' },
        { label: 'USAGERS VULNÉRABLES', desc: 'Piétons, cyclistes, enfants, EDPM…' },
        { label: 'VISIBILITÉ', desc: 'Zones masquées, angles morts.' },
        { label: 'INFRASTRUCTURE', desc: 'Panneaux, marquages, voies, sorties.' },
        { label: 'ENVIRONNEMENT', desc: 'Météo, luminosité, végétation, animaux.' },
        { label: 'SIGNALISATION', desc: 'Feux, priorités, limitations.' },
        { label: 'ÉTAT DE LA ROUTE', desc: 'Adhérence, gravillons, défauts, travaux.' },
      ],
      retenir: 'CHERCHER LES INDICES, C\'EST ANTICIPER. PLUS TU COMPRENDS TÔT, PLUS TU AS LE CHOIX.',
    },
    {
      num: '05',
      kicker: 'Allure',
      title: 'Tu adaptes ton allure avant le danger',
      image: '/images/articles/cours2-c5.png',
      imageAlt: 'Tu adaptes ton allure avant le danger — Code 2 Roues',
      lead: 'Nous ne cherchons pas à ralentir devant chaque situation, mais à adapter ton allure suffisamment tôt lorsque la situation l\'exige.',
      type: 'doDont',
      dont: {
        title: 'DANGER → SURPRISE → FREINAGE',
        desc: 'Réaction tardive, stress, distance de freinage plus longue, risque élevé.',
      },
      doItem: {
        title: 'INFORMATION → ANTICIPATION → ADAPTATION → DÉCISION',
        desc: 'Réaction précoce, maîtrise, choix, marge de sécurité.',
      },
      retenir: 'RÉDUIRE SA VITESSE AU BON MOMENT, C\'EST ÉVITER D\'AVOIR À FREINER AU MAUVAIS. ANTICIPER, C\'EST SE DONNER DU TEMPS ET DES OPTIONS.',
    },
    {
      num: '06',
      kicker: 'Espace de sécurité',
      title: 'Tu construis ton espace de sécurité',
      image: '/images/articles/cours2-c6.png',
      imageAlt: 'Tu construis ton espace de sécurité — Code 2 Roues',
      lead: 'Ton espace autour de la moto constitue une véritable réserve de sécurité. Tu apprends à conserver :',
      type: 'list',
      list: [
        { label: 'DE L\'ESPACE DEVANT TOI', desc: 'Pour anticiper et freiner.' },
        { label: 'DE L\'ESPACE LATÉRALEMENT', desc: 'Pour éviter les zones à risque.' },
        { label: 'UNE POSSIBILITÉ DE DÉGAGEMENT', desc: 'Lorsque la situation le permet.' },
      ],
      body: 'Tu évites de rester inutilement : derrière un véhicule sans visibilité, à côté d\'un véhicule, dans l\'angle mort d\'un poids lourd ou trop près d\'un véhicule stationné.',
      retenir: 'TON ESPACE = TON TEMPS DE RÉACTION + TES POSSIBILITÉS D\'ACTION.',
    },
    {
      num: '07',
      kicker: 'Intersections',
      title: 'Tu lis les intersections',
      image: '/images/articles/cours2-c7.png',
      imageAlt: 'Tu lis les intersections — Code 2 Roues',
      lead: 'Cette fois, nous allons plus loin que simplement respecter une priorité. Avant l\'intersection, tu recherches :',
      type: 'list',
      list: [
        { label: 'OÙ EST-ELLE ?', desc: 'Repérer sa présence et son type.' },
        { label: 'QUELLE PRIORITÉ ?', desc: 'Panneaux, marquages, feux.' },
        { label: 'QU\'EST-CE QUE JE VOIS ?', desc: 'Véhicules, piétons, indices.' },
        { label: 'QU\'EST-CE QUE JE NE VOIS PAS ?', desc: 'Zones masquées, angles morts.' },
        { label: 'QUI PEUT ARRIVER ?', desc: 'Anticiper les autres usagers.' },
        { label: 'M\'A-T-ON VU ?', desc: 'Vérifier ta visibilité.' },
        { label: 'MON ALLURE ME PERMET-ELLE DE RÉAGIR ?', desc: 'Adapter en amont.' },
      ],
      body: 'Nous conservons notre méthode : DÉTECTE → ADAPTE → OBSERVE → DÉCIDE → AGIS.',
      retenir: 'CE QUE TU NE VOIS PAS PEUT AUSSI REPRÉSENTER UN DANGER.',
    },
    {
      num: '08',
      kicker: 'Giratoires',
      title: 'Tu travailles les giratoires en autonomie',
      image: '/images/articles/cours2-c8.png',
      imageAlt: 'Tu travailles les giratoires en autonomie — Code 2 Roues',
      lead: 'Lors de la première séance, nous expliquons et accompagnons. Maintenant, tu commences à préparer toi-même ton giratoire :',
      type: 'timeline',
      steps: [
        { time: '1', label: 'MA SORTIE', desc: 'J\'identifie ma direction à l\'avance.' },
        { time: '2', label: 'MA SIGNALISATION', desc: 'Panneaux, marquages, priorités.' },
        { time: '3', label: 'MON ALLURE', desc: 'Adaptée avant l\'entrée.' },
        { time: '4', label: 'MES CONTRÔLES', desc: 'Regards, angles morts, environnement.' },
        { time: '5', label: 'MON PLACEMENT', desc: 'Dans la bonne voie, à la bonne distance.' },
        { time: '6', label: 'MON ENGAGEMENT', desc: 'Décision claire et communication.' },
        { time: '7', label: 'MA SORTIE', desc: 'Contrôles, clignotant, dégagement.' },
      ],
      retenir: 'TON ENSEIGNANT ANNONCE LA DIRECTION. C\'EST À TOI DE CONSTRUIRE PROGRESSIVEMENT LE RESTE.',
    },
    {
      num: '09',
      kicker: 'Usagers vulnérables',
      title: 'Tu prends en compte les usagers vulnérables',
      image: '/images/articles/cours2-c9.png',
      imageAlt: 'Tu prends en compte les usagers vulnérables — Code 2 Roues',
      lead: 'Piétons, cyclistes, enfants, utilisateurs d\'EDPM et autres deux-roues nécessitent une vigilance particulière. Tu recherches notamment :',
      type: 'list',
      list: [
        { label: 'LEUR PRÉSENCE', desc: 'Repérer chaque usager sur la scène.' },
        { label: 'LEUR TRAJECTOIRE POSSIBLE', desc: 'Anticiper leurs déplacements.' },
        { label: 'LEUR VISIBILITÉ', desc: 'Sont-ils vus par les autres ?' },
        { label: 'LEURS CHANGEMENTS DE DIRECTION', desc: 'Attention aux gestes soudains.' },
        { label: 'ZONES OÙ ILS PEUVENT ÊTRE MASQUÉS', desc: 'Véhicules stationnés, obstacles.' },
      ],
      retenir: 'TU ADAPTES TON ALLURE ET TON ESPACE AVANT D\'ÊTRE AU CONTACT DE LA SITUATION.',
    },
    {
      num: '10',
      kicker: 'Lecture des véhicules',
      title: 'Tu apprends à lire les véhicules',
      image: '/images/articles/cours2-c10.png',
      imageAlt: 'Tu apprends à lire les véhicules — Code 2 Roues',
      lead: 'Une voiture ne communique pas uniquement avec ses clignotants. Son comportement te donne des indices. Tu observes notamment :',
      type: 'list',
      list: [
        { label: 'SON ALLURE', desc: 'Ralentit-il ? Accélère-t-il ?' },
        { label: 'SA POSITION', desc: 'Se décale-t-il ?' },
        { label: 'SES ROUES', desc: 'Orientées vers où ?' },
        { label: 'SES FEUX STOP', desc: 'Il freine, pourquoi ?' },
        { label: 'SON CONDUCTEUR', desc: 'M\'a-t-il vu ? Regarde-t-il ?' },
        { label: 'SON ORIENTATION', desc: 'Vers une sortie, une voie ?' },
      ],
      retenir: 'TU NE CONFONDS JAMAIS UNE INTENTION AFFICHÉE AVEC UNE ACTION CERTAINE. TU VÉRIFIES CE QUE FAIT RÉELLEMENT L\'USAGER.',
    },
    {
      num: '11',
      kicker: 'Leader',
      title: 'Le leader prend davantage de responsabilité pédagogique',
      image: '/images/articles/cours2-c11.png',
      imageAlt: 'Le leader prend davantage de responsabilité pédagogique — Code 2 Roues',
      lead: 'Le rôle du leader devient particulièrement intéressant pendant ce deuxième cours. Lorsque tu es leader, tu dois simultanément :',
      type: 'list',
      list: [
        { label: 'LIRE LA ROUTE DEVANT TOI', desc: 'Rechercher activement les indices.' },
        { label: 'ANTICIPER', desc: 'Détecter les situations en amont.' },
        { label: 'MAINTENIR UNE ALLURE COHÉRENTE', desc: 'Pour la sécurité du groupe.' },
        { label: 'PRENDRE TES DÉCISIONS', desc: 'Sans attendre la consigne.' },
        { label: 'CONSERVER LA COHÉSION DU GROUPE', desc: 'Rythme et communication.' },
      ],
      retenir: 'ON PEUT FAIRE TOURNER LE RÔLE. CHAQUE ÉLÈVE EXPÉRIMENTE LA DIFFÉRENCE ENTRE SUIVRE LE GROUPE ET CONDUIRE LE GROUPE.',
    },
    {
      num: '12',
      kicker: 'Guidage progressif',
      title: 'Ton enseignant parle moins',
      image: '/images/articles/cours2-c12.png',
      imageAlt: 'Ton enseignant parle moins — Code 2 Roues',
      lead: 'C\'est une évolution importante. Le guidage diminue progressivement :',
      type: 'timeline',
      steps: [
        { time: 'Étape 1', label: 'CONSIGNE DIRECTE', desc: '« Attention, intersection devant. Adapte ton allure. »' },
        { time: 'Étape 2', label: 'QUESTION OUVERTE', desc: '« Qu\'est-ce que tu vois ? »' },
        { time: 'Étape 3', label: 'INVITATION À L\'ANALYSE', desc: '« Analyse la situation. »' },
        { time: 'Étape 4', label: 'AUTONOMIE', desc: 'Ton enseignant observe. Si tu détectes, analyses et adaptes seul : tu commences à devenir autonome.' },
      ],
      retenir: 'LA RADIO RESTE DISPONIBLE POUR L\'ITINÉRAIRE, LE TRAVAIL PÉDAGOGIQUE ET NATURELLEMENT LA SÉCURITÉ.',
    },
    {
      num: '13',
      kicker: 'Liaison livret',
      title: 'Les situations rencontrées deviennent ton cours',
      image: '/images/articles/cours2-c13.png',
      imageAlt: 'Les situations rencontrées deviennent ton cours — Code 2 Roues',
      lead: 'Comme pendant la première séance, nous utilisons la circulation réelle. Mais cette fois, ton enseignant peut davantage te questionner :',
      type: 'list',
      list: [
        { label: '« QUEL RISQUE IDENTIFIES-TU ? »', desc: 'Tu analyses activement.' },
        { label: '« POURQUOI MODIFIES-TU TON ALLURE ? »', desc: 'Tu justifies tes actions.' },
        { label: '« OÙ REGARDES-TU ? »', desc: 'Tu prends conscience de ton regard.' },
        { label: '« POURQUOI CHOISIS-TU CETTE POSITION ? »', desc: 'Tu expliques ton placement.' },
        { label: '« QU\'EST-CE QUE CETTE VOITURE PEUT FAIRE ? »', desc: 'Tu anticipes les autres usagers.' },
      ],
      retenir: 'TU NE MÉMORISES PAS SEULEMENT UNE RÉPONSE. TU APPRENDS À EXPLIQUER TA DÉCISION.',
    },
    {
      num: '14',
      kicker: 'Bilan',
      title: 'Ton bilan n°2',
      image: '/images/articles/cours2-c14.png',
      imageAlt: 'Ton bilan n°2 — Code 2 Roues',
      lead: 'En fin de séance, nous revenons sur ton objectif avec une auto-évaluation qui évolue :',
      type: 'bilan',
      questions: [
        'Détecter suffisamment tôt ?',
        'Rechercher les indices ?',
        'Adapter mon allure avant la situation ?',
        'Conserver un espace de sécurité ?',
        'Analyser une intersection ?',
        'Préparer seul un giratoire ?',
        'Expliquer certaines de mes décisions ?',
      ],
      blocks: [
        { title: 'CE QUE TU MAÎTRISES', color: 'green' },
        { title: 'CE QUI PROGRESSE', color: 'amber' },
        { title: 'TON OBJECTIF N°3', color: 'red' },
      ],
      retenir: 'NE REGARDE PAS SEULEMENT LA ROUTE. APPRENDS À LA LIRE. VOIS PLUS TÔT. COMPRENDS PLUS VITE. AGIS PLUS SEREINEMENT.',
    },
  ],
};
